<script lang="ts">
  import { site } from '$lib/config/site.config';

  const subject = `Feedback for ${site.brandName}`;
  const body = [
    'Hi SPA Studio,',
    '',
    'I wanted to share feedback about:',
    '',
    'What happened:',
    '',
    'What I expected:',
    '',
    'Browser/device:',
    ''
  ].join('\n');

  const encodedEmail = encodeURIComponent(site.contactEmail);
  const encodedSubject = encodeURIComponent(subject);
  const encodedBody = encodeURIComponent(body);

  const mailtoHref = `mailto:${site.contactEmail}?subject=${encodedSubject}&body=${encodedBody}`;
  const gmailHref = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodedEmail}&su=${encodedSubject}&body=${encodedBody}`;
  const outlookHref = `https://outlook.live.com/mail/0/deeplink/compose?to=${encodedEmail}&subject=${encodedSubject}&body=${encodedBody}`;
</script>

<div class="feedback-page">
  <section class="feedback-hero">
    <p class="eyebrow">Feedback</p>
    <h1>Help improve {site.brandName}</h1>
    <p class="intro">
      Found a bug, missing tool, confusing workflow, or accessibility issue? Send a short note and include the page or tool name if it applies.
    </p>
  </section>

  <section class="feedback-card" aria-labelledby="feedback-options">
    <div>
      <h2 id="feedback-options">Send feedback</h2>
      <p>
        These options open your email app or webmail with a prefilled message to <strong>{site.contactEmail}</strong>.
      </p>
    </div>

    <div class="action-grid">
      <a class="action primary" href={mailtoHref}>
        <span>Default email app</span>
        <small>Uses your browser or device email settings</small>
      </a>
      <a class="action" href={gmailHref} target="_blank" rel="noreferrer">
        <span>Gmail</span>
        <small>Opens Gmail compose in a new tab</small>
      </a>
      <a class="action" href={outlookHref} target="_blank" rel="noreferrer">
        <span>Outlook</span>
        <small>Opens Outlook compose in a new tab</small>
      </a>
    </div>
  </section>

  <section class="guidance">
    <h2>Useful details</h2>
    <ul>
      <li>The tool or page where you noticed the issue</li>
      <li>What you expected versus what happened</li>
      <li>Your browser, device, and whether the issue repeats</li>
    </ul>
  </section>
</div>

<style>
  .feedback-page {
    max-width: 760px;
    margin: 0 auto;
    padding: 2rem 1.5rem 4rem;
    color: hsl(var(--theme-foreground));
  }
  .feedback-hero {
    text-align: center;
    padding: 2rem 0 2.5rem;
  }
  .eyebrow {
    display: inline-flex;
    padding: 0.3rem 0.75rem;
    border-radius: 9999px;
    margin-bottom: 1rem;
    color: hsl(var(--theme-primary));
    background: hsl(var(--theme-primary) / 0.08);
    border: 1px solid hsl(var(--theme-primary) / 0.16);
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
  h1 {
    margin-bottom: 0.875rem;
    color: hsl(var(--theme-foreground));
    font-size: clamp(1.75rem, 4vw, 2.35rem);
    font-weight: 700;
    letter-spacing: -0.035em;
  }
  .intro {
    max-width: 560px;
    margin: 0 auto;
    color: hsl(var(--theme-muted-foreground));
    font-size: 0.9375rem;
    line-height: 1.7;
  }
  .feedback-card,
  .guidance {
    border: 1px solid hsl(var(--theme-border) / 0.5);
    background: hsl(var(--theme-card) / 0.72);
    border-radius: var(--theme-radius-lg);
    box-shadow: var(--theme-shadow-sm);
  }
  .feedback-card {
    padding: 1.5rem;
  }
  h2 {
    margin-bottom: 0.5rem;
    color: hsl(var(--theme-foreground));
    font-size: 1.125rem;
    font-weight: 600;
    letter-spacing: -0.01em;
  }
  p,
  li {
    color: hsl(var(--theme-muted-foreground));
    font-size: 0.9375rem;
    line-height: 1.7;
  }
  strong { color: hsl(var(--theme-foreground)); font-weight: 600; }
  .action-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.75rem;
    margin-top: 1.25rem;
  }
  .action {
    display: flex;
    min-height: 96px;
    flex-direction: column;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 1rem;
    border-radius: var(--theme-radius);
    border: 1px solid hsl(var(--theme-border) / 0.7);
    background: hsl(var(--theme-muted) / 0.08);
    color: hsl(var(--theme-foreground));
    text-decoration: none;
    transition: all 200ms ease;
  }
  .action:hover {
    transform: translateY(-2px);
    border-color: hsl(var(--theme-primary) / 0.35);
    background: hsl(var(--theme-muted) / 0.14);
  }
  .action.primary {
    border-color: hsl(var(--theme-primary) / 0.35);
    background: hsl(var(--theme-primary) / 0.1);
  }
  .action span {
    font-size: 0.9375rem;
    font-weight: 600;
  }
  .action small {
    color: hsl(var(--theme-muted-foreground));
    font-size: 0.75rem;
    line-height: 1.45;
  }
  .guidance {
    margin-top: 1rem;
    padding: 1.25rem 1.5rem;
  }
  ul {
    margin: 0.75rem 0 0;
    padding-left: 1.25rem;
  }
  li { margin-bottom: 0.375rem; }

  @media (max-width: 700px) {
    .feedback-page { padding: 1.5rem 1rem 3rem; }
    .action-grid { grid-template-columns: 1fr; }
  }
</style>
